<template>
  <section
    class="preview-pane flex-grow-1 d-flex flex-column h-100"
    :class="{ 'is-public-user': isPublicLoggedIn, 'incomplete-practice': !user.practice.is_setup_complete }"
    :key="isAnyLoggedIn"
  >
    <ReferralLoader v-if="!isTokenAvailable || isAuthing" :showPractice="toPractice" />
    <template v-else>
      <!-- {{ user }} {{ referral }} -->
      <provide-email
        class="fixed-top"
        v-if="user.practice && !user.practice.email && referral && referral.sent_time"
        :referral="referral" />
      <template v-if="isPracticeChoice">
        <public-office-selection />
      </template>
      <template v-else-if="isEditor">
        <button @click="sendClick" class="btn btn-lg btn-xl btn-primary send-button" :disabled="isSubmitting">
          <font-awesome-icon icon="spinner-third" v-if="isSubmitting" spin />
          <span v-else>Send</span>
        </button>
        <!-- <ReferralTopBar :referralId="referralId" @send="sendClick" :isSaving="isSaving" /> -->
        <ReferralEditor
          ref="referralEditor"
          :key="$route.path + isTokenAvailable"
          :redirectOnCreate="`/send/${$route.params.to || 0}/`"
          :allowPracticeChange="!$route.params.to || $route.params.to === '0'"
          :showSidebar="false"
          :showAppointmentPicker="false"
          :showSendModal="isConfirmationModalVisible"
          @closeSendModal="isConfirmationModalVisible = false"
          @isSaving="isSaving = $event"
          style="overflow: auto"
      /></template>
      <template v-else>
        <sticky-toolbar :referralId="referralId" />
        <ReferralPreview
          :item="referral"
          :showSidebar="false"
          style="overflow: auto"
          :class="{ 'pt-3': !user.practice.is_setup_complete }"
          ref="refReferralPreview"
        />
      </template>
      <success-modal
        v-if="referral && isSuccessModalShown"
        @close="isSuccessModalShown = false"
        :message="`This referral has been delivered to ${referral.to_practice && referral.to_practice.name}`"
    /></template>
  </section>
</template>
  <script>
import { mapState, mapGetters } from 'vuex'
import ReferralTopBar from '@/components/referrals/inbox/ReferralTopBar'
import ReferralLoader from '@/components/referrals/inbox/ReferralLoader'
import ReferralEditor from '@/views/referrals/ReferralEditor'
import ReferralPreview from '@/components/referrals/inbox/ReferralPreview'
import Empty from '@/components/shared/Empty.vue'
import SuccessModal from '../../components/shared/SuccessModal.vue'
import { captureException } from '@sentry/minimal'
import { PracticeApi, ReferralApi } from '../../services'
import Onboard from '../auth/Onboard.vue'
import ProvideEmail from '../auth/ProvideEmail.vue'
import Modal from '../../components/Modal.vue'
import UserAuth from '../auth/UserAuth.vue'
import PublicOfficeSelection from './PublicOfficeSelection.vue'
import StickyToolbar from './StickyToolbar.vue'

export default {
  props: {
    showIncoming: { default: true },
  },
  components: {
    ReferralTopBar,
    ReferralLoader,
    ReferralEditor,
    ReferralPreview,
    Empty,
    SuccessModal,
    Onboard,
    ProvideEmail,
    Modal,
    UserAuth,
    PublicOfficeSelection,
    StickyToolbar,
  },
  data() {
    return {
      isConfirmationModalVisible: false,
      isSaving: false,
      isAuthing: false,
      authError: '',
      toPractice: null,
      isRegisterModalVisible: false,
      isLogInShown: false,
      isSubmitting: false,
    }
  },
  async created() {},
  async mounted() {
    this.isAuthing = true
    if (!this.isTokenAvailable) {
      await this.createGuestUser()
    }
    if (this.toPracticeSlugOrId?.length == 1) {
      try {
        // mainly just to show the logo during load
        this.toPractice = await PracticeApi.get(this.toPracticeSlugOrId[0], { anonymous: true })
        //since the /referrals endpoint doesnt include doctors (only search practice)
        //and their isnt a network relationsip; we need to loadDoctors for the editor to have access
        this.$store.dispatch('practices/loadDoctors', this.toPracticeSlugOrId[0])
      } catch (err) {
        this.$helpers.error('Failed to load office information. Check the link and try again.')
        this.$router.replace('/send')
      }
    }

    this.isAuthing = false
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['isLoggedIn', 'isPublicLoggedIn', 'isTokenAvailable', 'isOrganization']),
    referralId() {
      return this.$route.params?.id
    },
    referral() {
      return this.referralId ? this.$store.getters['referrals/get'](this.referralId) : { to_practice: { name: 'test' } }
    },
    isPracticeChoice() {
      return this.toPracticeSlugOrId?.length > 1
    },
    isEditor() {
      return !this.referral?.sent_time
    },
    isSuccessModalShown: {
      get() {
        return this.$store.state.referrals.isSuccessModalShown
      },
      set(val) {
        this.$store.dispatch('referrals/setIsSuccessModalShown', val)
      },
    },
    isAnyLoggedIn() {
      return true || this.isLoggedIn || this.isPublicLoggedIn
    },
    toPracticeSlugOrId() {
      if (this.toPractice) {
        return [this.toPractice.id]
      }

      if (this.$route.params.to && this.$route.params.to !== '0') {
        return this.$route.params.to.split(',')
      }

      return undefined
    },
  },
  watch: {
    'referral.sent_time': function (n, o) {
      if (!n) return
      this.$nextTick(() => {
        this.$refs.refReferralPreview.$el.scrollIntoView({ behavior: 'smooth' })
      })
    },
  },
  methods: {
    async createGuestUser() {
      try {
        await this.$store.dispatch('auth/loginPublic')
      } catch (err) {
        this.authError = err
      }
      try {
        await this.$store.dispatch('auth/me')
      } catch (err) {
        this.authError = err
      }
    },
    async sendClick() {
      this.isSubmitting = true
      await this.$refs.referralEditor.localSave()
      this.$refs.referralEditor.configureValidation({
        events: 'change|input|blur',
      })
      const isValid = await this.$refs.referralEditor.validate()
      if (isValid) {
        if (this.isPublicLoggedIn) {
          try {
            await this.$store.dispatch('auth/promotePublicUser', {
              doctor: this.referral.referring_doctor,
              referralId: this.referralId,
            })
            const ref = await ReferralApi.get(this.referralId)
            await this.$store.dispatch('referrals/save', ref)
          } catch (err) {
            this.isSubmitting = false
            this.$helpers.error('Failed to onboard user')
            console.error(err)
            captureException(err)
            return
          }
        }
        this.isSubmitting = false
        this.isConfirmationModalVisible = true
      }
    },
  },
  // async beforeRouteEnter(to, from, next) {
  //   if (to.query.to) {
  //     const referring_doctor_id =
  //       store.state.auth.user.practice.doctors.length &&
  //       store.state.auth.user.practice.doctors[0].id
  //     const r = await store.dispatch('referrals/save', {
  //       to_practice_id: to.query.to,
  //       referring_doctor_id,
  //     })
  //     next('/referral/' + r.id)
  //   } else next()
  // },
}
</script>
<style lang="scss" scoped>
.send-button {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: 999;
}
.fixed-top {
  position: fixed;
  top: auto;
  bottom: 0;
  width: 100%;
  z-index: 999;
}
</style>